<script setup lang="ts">
interface Props {
  itemsToShow?: number;
  breakpoints?: [number, number][];
  autoplay?: boolean;
  autoplayInterval?: number;
  loop?: boolean;
  gap?: number;
  /**
   * @deprecated Edge fade overlay removed — kept on the props interface so
   * existing call-sites (`:fade-edges="true"`) don't error. Has no effect.
   */
  fadeEdges?: boolean;
  navigationEnabled?: boolean;
  navPosition?: "center" | "top-right" | "outside";
}

const props = withDefaults(defineProps<Props>(), {
  itemsToShow: 1,
  breakpoints: undefined,
  autoplay: false,
  autoplayInterval: 5000,
  loop: true,
  navigationEnabled: true,
  // Tight inter-slide gap (was 16, then 6). Slide width is computed
  // from (containerWidth - totalGap) / items so cards still tile the
  // full row at this tighter spacing.
  gap: 2,
  fadeEdges: false,
  navPosition: "center",
});

const emit = defineEmits<{
  slideChange: [index: number];
}>();

const trackRef = ref<HTMLElement | null>(null);
const containerRef = ref<HTMLElement | null>(null);
const currentIndex = ref(0);
const totalSlides = ref(0);
const isDragging = ref(false);
const windowWidth = ref(0);

let startX = 0;
let autoplayTimer: ReturnType<typeof setInterval> | null = null;

const effectiveItemsToShow = computed(() => {
  if (!props.breakpoints || !props.breakpoints.length) return props.itemsToShow;
  const sorted = [...props.breakpoints].sort((a, b) => a[0] - b[0]);
  let result = sorted[0]?.[1] ?? props.itemsToShow;
  for (const [breakpoint, count] of sorted) {
    if (windowWidth.value >= breakpoint) result = count;
  }
  return result;
});

const maxIndex = computed(() =>
  Math.max(0, totalSlides.value - Math.floor(effectiveItemsToShow.value)),
);
const canPrev = computed(() => props.loop || currentIndex.value > 0);
const canNext = computed(() => props.loop || currentIndex.value < maxIndex.value);
const showNav = computed(
  () => totalSlides.value > effectiveItemsToShow.value && props.navigationEnabled,
);

onMounted(() => {
  windowWidth.value = window.innerWidth;
  countSlides();
  sizeSlides();
  if (props.autoplay) startAutoplay();
  window.addEventListener("resize", handleResize);
});

onUnmounted(() => {
  stopAutoplay();
  window.removeEventListener("resize", handleResize);
});

function handleResize() {
  windowWidth.value = window.innerWidth;
  sizeSlides();
  if (currentIndex.value > maxIndex.value) {
    goTo(maxIndex.value, false);
  } else {
    goTo(currentIndex.value, false);
  }
}

function countSlides() {
  if (trackRef.value) {
    totalSlides.value = trackRef.value.children.length;
  }
}

function sizeSlides() {
  if (!trackRef.value || !containerRef.value) return;
  const containerWidth = containerRef.value.offsetWidth;
  const items = effectiveItemsToShow.value;
  const totalGap = props.gap * (Math.floor(items) - 1);
  // Ceil instead of floor: pushes the slide AFTER the last visible one
  // fully past the viewport edge. Trade-off is that the last visible
  // slide may overflow by up to (gap) px, which `overflow: hidden` on
  // the viewport clips invisibly — better than a 1–3px sliver of the
  // next card peeking in at the right edge on desktop 4-up layouts.
  const slideWidth = Math.ceil((containerWidth - totalGap) / items);
  const children = trackRef.value.children;
  for (let i = 0; i < children.length; i++) {
    const child = children[i] as HTMLElement;
    child.style.width = `${slideWidth}px`;
    child.style.minWidth = `${slideWidth}px`;
    child.style.maxWidth = `${slideWidth}px`;
  }
}

function goTo(index: number, animate = true) {
  if (!trackRef.value || !containerRef.value) return;

  let newIndex = index;
  if (props.loop) {
    if (newIndex < 0) newIndex = maxIndex.value;
    if (newIndex > maxIndex.value) newIndex = 0;
  } else {
    newIndex = Math.max(0, Math.min(newIndex, maxIndex.value));
  }

  currentIndex.value = newIndex;

  const containerWidth = containerRef.value.offsetWidth;
  const items = effectiveItemsToShow.value;
  const totalGap = props.gap * (Math.floor(items) - 1);
  const slideWidth = Math.ceil((containerWidth - totalGap) / items);
  const offset = newIndex * (slideWidth + props.gap);

  trackRef.value.style.transition = animate ? "transform 0.3s ease" : "none";
  trackRef.value.style.transform = `translateX(-${offset}px)`;

  emit("slideChange", newIndex);
}

function prev() {
  goTo(currentIndex.value - 1);
}

function next() {
  goTo(currentIndex.value + 1);
}

function startAutoplay() {
  stopAutoplay();
  autoplayTimer = setInterval(() => {
    next();
  }, props.autoplayInterval);
}

function stopAutoplay() {
  if (autoplayTimer) {
    clearInterval(autoplayTimer);
    autoplayTimer = null;
  }
}

function onPointerDown(e: PointerEvent) {
  isDragging.value = true;
  startX = e.clientX;
  stopAutoplay();
}

function onPointerMove(e: PointerEvent) {
  if (!isDragging.value) return;
  e.preventDefault();
}

function onPointerUp(e: PointerEvent) {
  if (!isDragging.value) return;
  isDragging.value = false;

  const diff = e.clientX - startX;
  const threshold = 50;
  if (diff > threshold) {
    prev();
  } else if (diff < -threshold) {
    next();
  }

  if (props.autoplay) startAutoplay();
}

defineExpose({ prev, next, goTo, currentIndex, totalSlides });
</script>

<template>
  <div
    ref="containerRef"
    class="carousel-container"
  >
    <div
      class="carousel-container__viewport"
      @pointerdown="onPointerDown"
      @pointermove="onPointerMove"
      @pointerup="onPointerUp"
      @pointerleave="onPointerUp"
    >
      <div
        ref="trackRef"
        class="carousel-container__track"
        :style="{ gap: `${gap}px` }"
      >
        <slot />
      </div>
    </div>

    <div
      v-if="showNav"
      class="carousel-container__navigation"
      :class="`carousel-container__navigation--${navPosition}`"
    >
      <button
        v-if="canPrev"
        class="carousel-container__navigation-button"
        aria-label="Previous"
        @click="prev"
      >
        <fa :icon="['fal', 'chevron-left']" />
      </button>
      <button
        v-if="canNext"
        class="carousel-container__navigation-button"
        aria-label="Next"
        @click="next"
      >
        <fa :icon="['fal', 'chevron-right']" />
      </button>
    </div>
  </div>
</template>

<style lang="scss">
.carousel-container {
  position: relative;
  width: 100%;

  &__viewport {
    width: 100%;
    box-sizing: border-box;
    overflow: hidden;
    position: relative;
    touch-action: pan-y;
    // Horizontal padding removed so card content lines up with section
    // titles and the rest of the page edge. Slim vertical padding kept
    // so the card box-shadow doesn't get clipped at the top/bottom.
    padding: 0.5rem 0;
  }

  &__track {
    display: flex;
    flex-direction: row;
    backface-visibility: hidden;
    will-change: transform;
  }

  &__navigation {
    position: absolute;
    pointer-events: none;
    display: flex;
    z-index: 10;

    @media (max-width: $tablet) {
      display: none;
    }

    &--center {
      top: 50%;
      left: 0;
      right: 0;
      width: 100%;
      transform: translateY(-50%);
      justify-content: space-between;
      padding: 0 1rem;
    }

    &--top-right {
      // Fixed-width anchor on the right edge — % width was scaling with
      // the wider page (no sidebar) and pushing the two arrows apart.
      // rem keeps them snug together and right-aligned with the section.
      top: -2.75rem;
      width: 4.5rem;
      right: 0;
      left: auto;
      justify-content: space-between;
    }

    &--outside {
      top: 50%;
      left: -5%;
      right: 0;
      width: 107%;
      transform: translateY(-50%);
      justify-content: space-between;
      padding: 0 1rem;
    }
  }

  // Carousel arrows: borderless chevron, no circle outline. Just the icon
  // sitting in the row — small bump in size on hover for affordance.
  &__navigation-button {
    appearance: none;
    background: transparent;
    border: none;
    color: var(--color-grey-1) !important;
    cursor: pointer;
    pointer-events: auto;
    align-items: center;
    display: flex;
    font-weight: var(--weight-semibold);
    height: 2rem;
    justify-content: center;
    padding: 0 !important;
    width: 2rem;
    transition: color var(--dur-fast) var(--ease-out), transform 100ms var(--ease-out);
    &:disabled {
      opacity: 0.3;
      cursor: default;
    }
    &:hover:not(:disabled) {
      color: var(--color-pink-1) !important;
      transform: scale(1.1);
    }
    svg {
      font-size: 1rem !important;
    }
  }

}
</style>
